import { z } from "zod";
import {
    MessageSchema,
    SubthreadSchema,
    Thread,
    ThreadWithLastMessageSchema,
} from "@common/types/chat";
import { SpreadsheetReferenceSchema } from "@common/schemas/spreadsheetReference.schema";
import {
    MAX_TRANSACTION_REFERENCES,
    TransactionReferenceSchema,
} from "@common/schemas/transactionReference.schema";
import {
    CounterpartyReferenceSchema,
    MAX_COUNTERPARTY_REFERENCES,
} from "@common/schemas/counterpartyReference.schema";
import {
    InvoiceReferenceSchema,
    MAX_INVOICE_REFERENCES,
} from "@common/schemas/invoiceReference.schema";
import {
    DocumentReferenceSchema,
    MAX_DOCUMENT_REFERENCES,
} from "@common/schemas/documentReference.schema";

export const optionalSpreadsheetReferencesSchema = z
    .array(SpreadsheetReferenceSchema)
    .max(10)
    .optional();

export const optionalTransactionReferencesSchema = z
    .array(TransactionReferenceSchema)
    .max(MAX_TRANSACTION_REFERENCES)
    .optional();

export const optionalCounterpartyReferencesSchema = z
    .array(CounterpartyReferenceSchema)
    .max(MAX_COUNTERPARTY_REFERENCES)
    .optional();

export const optionalInvoiceReferencesSchema = z
    .array(InvoiceReferenceSchema)
    .max(MAX_INVOICE_REFERENCES)
    .optional();

export const optionalDocumentReferencesSchema = z
    .array(DocumentReferenceSchema)
    .max(MAX_DOCUMENT_REFERENCES)
    .optional();

// The composer caps staged files at 10, but a single file (e.g. a multi-tab
// spreadsheet/CSV) can expand into several financial documents on upload, so
// this array is derived from the flattened upload results and can exceed the
// file count. The cap is set well above 10 so a valid ≤10-file send is never
// rejected after the documents were already uploaded.
export const optionalFinancialDocumentIdsSchema = z
    .array(z.number().int().positive())
    .max(50)
    .optional();

export const ListThreadsResponseSchema = z.object({
    threads: z.array(ThreadWithLastMessageSchema),
    nextCursor: z.string().optional(),
    hasMore: z.boolean(),
});

export const OrganizationChatQuerySchema = z.object({
    organizationId: z.string().uuid(),
});

export const ListOrganizationThreadsQuerySchema =
    OrganizationChatQuerySchema.extend({
        limit: z.coerce.number().int().min(1).max(100).default(20),
        cursor: z.string().optional(),
    });

export const OrganizationThreadWithLastMessageSchema =
    ThreadWithLastMessageSchema.extend({ workspaceName: z.string() });

export type ThreadWithWorkspaceName = Thread &
    Partial<
        Pick<
            z.infer<typeof OrganizationThreadWithLastMessageSchema>,
            "workspaceName"
        >
    >;

export const ListOrganizationThreadsResponseSchema =
    ListThreadsResponseSchema.extend({
        threads: z.array(OrganizationThreadWithLastMessageSchema),
    });

export const ListMessagesResponseSchema = z.object({
    messages: z.array(MessageSchema),
    nextCursor: z.string().optional(),
    hasMore: z.boolean(),
});

export const ListSubthreadMessagesResponseSchema = ListMessagesResponseSchema;

export const GetOrCreateSubthreadResponseSchema = z.object({
    subthread: SubthreadSchema,
});

export const SendSubthreadMessageResponseSchema = z.object({
    message: MessageSchema,
});

export const ResolveChatMessageResponseSchema = z.object({
    message: MessageSchema,
});

export const ChatReactionMessageResponseSchema = z.object({
    message: MessageSchema,
});

export const ChatReactionPathParamsSchema = z.object({
    workspaceId: z.string().uuid(),
    threadId: z.string().uuid(),
    messageId: z.string().uuid(),
});

export const MarkAllChatMessagesReadResponseSchema = z.object({
    ok: z.literal(true),
    threadCount: z.number().int().min(0),
});
